import React, { useCallback, useState, useRef, Component } from 'react';
import { C, cardStyle, cardHeaderStyle, cardBodyStyle, labelStyle } from './form/constants';
import { FLabel, FInput, FTextarea, FSelect, CheckItem, CheckBox, Grid, FormGroup, Divider } from './form/FormControls';
import { SectionHeader, StandaloneTitle } from './form/SectionComponents';
import { Modal } from './form/Modal';
import { Btn } from './form/Btn';
interface FormState {
  areYou: string;
  location: string;
  admissionDate: string;
  reference: string;
  name: string;
  gender: string;
  age: string;
  mobile: string;
  guardianName: string;
  occupation: string;
  address: string;
  city: string;
  pincode: string;
  chiefComplaints: string;
  thirst: string;
  appetite: string;
  sleep: string;
  dreams: string;
  addiction: string;
  bp: string;
  weight: string;
  temperature: string;
  hygiene: string;
  lifestyle: string;
  bodyType: string;
  bodyPalpation: string;
  tongue: string;
  pulse: string;
  pastIllness: string;
  bowel: string;
  urineFrequency: string;
  urineFrequencyNight: string;
  urineColor: string;
  urineSmell: string;
  urine: string;
  skin: string;
  scalp: string;
  bodyHair: string;
  nails: string;
  bodyOdour: string;
  edema: string;
  eye: string;
  ear: string;
  mouthOdour: string;
  mouth: string;
  throat: string;
  lips: string;
  isPregnant: string;
  pregnancyMonths: string;
  menopause: string;
  mensurationDuration: string;
  mensurationCycleDays: string;
  mensurationClots: string;
  mensurationFlow: string;
  familyHistory: string;
  labInvestigations: string;
}
interface ChecksState {
  mentalCondition: string[];
  foodHabits: string[];
  medicalHistory: string[];
  gynConditions: string[];
  mensurationPainful: string[];
}
interface UploadedFile {
  name: string;
  data: string;
  type: string;
}
export function AIIMRForm() {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState<FormState>({
    areYou: '',
    location: '',
    admissionDate: today,
    reference: '',
    name: '',
    gender: '',
    age: '',
    mobile: '',
    guardianName: '',
    occupation: '',
    address: '',
    city: '',
    pincode: '',
    chiefComplaints: '',
    thirst: '',
    appetite: '',
    sleep: '',
    dreams: '',
    addiction: '',
    bp: '',
    weight: '',
    temperature: '',
    hygiene: '',
    lifestyle: '',
    bodyType: '',
    bodyPalpation: '',
    tongue: '',
    pulse: '',
    pastIllness: '',
    bowel: '',
    urineFrequency: '',
    urineFrequencyNight: '',
    urineColor: '',
    urineSmell: '',
    urine: '',
    skin: '',
    scalp: '',
    bodyHair: '',
    nails: '',
    bodyOdour: '',
    edema: '',
    eye: '',
    ear: '',
    mouthOdour: '',
    mouth: '',
    throat: '',
    lips: '',
    isPregnant: 'No',
    pregnancyMonths: '',
    menopause: 'No',
    mensurationDuration: '',
    mensurationCycleDays: '',
    mensurationClots: '',
    mensurationFlow: '',
    familyHistory: '',
    labInvestigations: ''
  });
  const [checks, setChecks] = useState<ChecksState>({
    mentalCondition: [],
    foodHabits: [],
    medicalHistory: [],
    gynConditions: [],
    mensurationPainful: []
  });
  const [mentalOther, setMentalOther] = useState('');
  const [showMentalOther, setShowMentalOther] = useState(false);
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [attachError, setAttachError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Modal states
  const [consentModal, setConsentModal] = useState(false);
  const [chartsModal, setChartsModal] = useState(false);
  const [waImagesModal, setWaImagesModal] = useState(false);
  const [tgSendModal, setTgSendModal] = useState(false);
  const [tgMessage, setTgMessage] = useState('');
  const [tgCopied, setTgCopied] = useState(false);
  const [toast, setToast] = useState('');
  const sf = (key: keyof FormState) => (val: string) => setForm(p => ({
    ...p,
    [key]: val
  }));
  const chk = (group: keyof ChecksState) => (val: string, checked: boolean) => setChecks(p => ({
    ...p,
    [group]: checked ? [...p[group], val] : p[group].filter(v => v !== val)
  }));
  // File handling
  const handleFiles = useCallback((files: FileList | null) => {
    setAttachError('');
    if (!files || files.length === 0) return;
    if (uploadedFiles.length + files.length > 10) {
      setAttachError('Maximum 10 files allowed.');
      return;
    }
    Array.from(files).forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        setAttachError(`"${file.name}" exceeds 5MB.`);
        return;
      }
      const reader = new FileReader();
      reader.onload = e => {
        setUploadedFiles(p => [...p, {
          name: file.name,
          data: e.target?.result as string,
          type: file.type
        }]);
      };
      reader.readAsDataURL(file);
    });
  }, [uploadedFiles.length]);
  // Validation
  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Patient name is required';
    if (!form.mobile.trim()) e.mobile = 'Mobile number is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  // Message builders
  const L = (label: string, v: string) => v ? `*${label}:* ${v}\n` : '';
  const A = (label: string, arr: string[] | undefined) => arr?.length ? `*${label}:* ${arr.join(', ')}\n` : '';
  const buildWhatsApp = () => {
    let m = `🏥 *AIIMR - Patient Registration Form*\n━━━━━━━━━━━━━━━━━━━━\n\n`;
    m += `👤 *PERSONAL DETAILS*\n`;
    m += L('Category', form.areYou);
    m += L('Branch', form.location);
    m += L('Admission Date', form.admissionDate);
    m += L('Reference', form.reference);
    m += L('Name', form.name);
    m += L('Gender', form.gender);
    m += L('Age', form.age);
    m += L('Mobile', form.mobile);
    m += L('Guardian', form.guardianName);
    m += L('Occupation', form.occupation);
    const addr = [form.address, form.city, form.pincode].filter(Boolean).join(', ');
    m += L('Address', addr);
    m += L('Chief Complaints', form.chiefComplaints);
    m += '\n';
    m += `🔬 *PHYSICAL EXAMINATION*\n`;
    m += L('Thirst', form.thirst);
    m += L('Appetite', form.appetite);
    m += L('Sleep', form.sleep);
    m += L('Dreams', form.dreams);
    m += A('Food Habits', checks.foodHabits);
    m += L('Hygiene', form.hygiene);
    m += L('Addiction', form.addiction);
    m += L('Lifestyle', form.lifestyle);
    m += L('Body Type', form.bodyType);
    m += L('Weight', form.weight ? form.weight + ' kg' : '');
    m += L('Temperature', form.temperature);
    m += L('Blood Pressure', form.bp);
    const mc = [...checks.mentalCondition];
    if (mc.includes('Other') && mentalOther) mc.push(`(Other: ${mentalOther})`);
    m += A('Mental Condition', mc);
    m += L('Body Palpation', form.bodyPalpation);
    m += L('Tongue', form.tongue);
    m += L('Pulse', form.pulse);
    m += '\n';
    m += `📋 *MEDICAL HISTORY*\n`;
    m += A('Known Conditions', checks.medicalHistory);
    m += L('Past Illness', form.pastIllness);
    m += '\n';
    m += `🔍 *OBJECTIVE FINDINGS*\n`;
    m += L('Bowel', form.bowel);
    m += L('Urine Day', form.urineFrequency);
    m += L('Urine Night', form.urineFrequencyNight);
    m += L('Urine Color', form.urineColor);
    m += L('Urine Smell', form.urineSmell);
    m += L('Urine Details', form.urine);
    m += L('Skin', form.skin);
    m += L('Scalp', form.scalp);
    m += L('Body Hair', form.bodyHair);
    m += L('Nails', form.nails);
    m += L('Body Odor', form.bodyOdour);
    m += L('Edema', form.edema);
    m += L('Eye', form.eye);
    m += L('Ear', form.ear);
    m += L('Mouth Odor', form.mouthOdour);
    m += L('Mouth', form.mouth);
    m += L('Throat', form.throat);
    m += L('Lips', form.lips);
    m += '\n';
    if (form.gender === 'F') {
      m += `👶 *GYNAECOLOGICAL HISTORY*\n`;
      m += A('Conditions', checks.gynConditions);
      m += L('Pregnant', form.isPregnant);
      if (form.isPregnant === 'Yes') m += L('Months', form.pregnancyMonths);
      if (form.isPregnant === 'No') {
        m += L('Menopause', form.menopause);
        if (form.menopause === 'No') {
          m += L('Duration', form.mensurationDuration);
          m += L('Cycle Days', form.mensurationCycleDays);
          m += L('Clots', form.mensurationClots);
          m += L('Flow', form.mensurationFlow);
          m += A('Painful', checks.mensurationPainful);
        }
      }
      m += '\n';
    }
    m += `🧪 *INVESTIGATIONS*\n`;
    m += L('Family History', form.familyHistory);
    m += L('Lab Investigations', form.labInvestigations);
    m += '\n';
    m += `━━━━━━━━━━━━━━━━━━━━\n_Submitted via AIIMR Patient Form_`;
    return m;
  };
  // Toast helper
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };
  // Submit handlers
  const handleWhatsApp = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validate()) return;
    if (!consentAgreed) {
      alert('Please agree to the Informed Consent Form before submitting.');
      return;
    }
    const msg = encodeURIComponent(buildWhatsApp());
    window.open(`https://wa.me/918303699990?text=${msg}`, '_blank');
    if (uploadedFiles.length > 0) {
      setTimeout(() => setWaImagesModal(true), 900);
    }
  };
  const handleTelegram = () => {
    if (!validate()) return;
    if (!consentAgreed) {
      alert('Please agree to the Informed Consent Form before submitting.');
      return;
    }
    const L2 = (label: string, v: string) => v ? `${label}: ${v}\n` : '';
    const A2 = (label: string, arr: string[] | undefined) => arr?.length ? `${label}: ${arr.join(', ')}\n` : '';
    let tgText = `🏥 AIIMR - Patient Registration Form\n${'─'.repeat(36)}\n\n`;
    tgText += `👤 PERSONAL DETAILS\n`;
    tgText += L2('Name', form.name);
    tgText += L2('Mobile', form.mobile);
    tgText += L2('Gender', form.gender);
    tgText += L2('Age', form.age);
    tgText += L2('Branch', form.location);
    tgText += L2('Date', form.admissionDate);
    tgText += L2('Occupation', form.occupation);
    const addr = [form.address, form.city, form.pincode].filter(Boolean).join(', ');
    tgText += L2('Address', addr);
    tgText += L2('Chief Complaints', form.chiefComplaints);
    tgText += `\n🔬 PHYSICAL EXAMINATION\n`;
    tgText += L2('Thirst', form.thirst);
    tgText += L2('Appetite', form.appetite);
    tgText += L2('Sleep', form.sleep);
    tgText += L2('BP', form.bp);
    tgText += L2('Weight', form.weight ? form.weight + ' kg' : '');
    tgText += A2('Mental Condition', checks.mentalCondition);
    tgText += L2('Tongue', form.tongue);
    tgText += L2('Pulse', form.pulse);
    tgText += `\n📋 MEDICAL HISTORY\n`;
    tgText += A2('Conditions', checks.medicalHistory);
    tgText += L2('Past Illness', form.pastIllness);
    tgText += `\n🔍 OBJECTIVE FINDINGS\n`;
    tgText += L2('Bowel', form.bowel);
    tgText += L2('Urine Color', form.urineColor);
    tgText += L2('Skin', form.skin);
    tgText += L2('Nails', form.nails);
    tgText += L2('Eye', form.eye);
    tgText += L2('Throat', form.throat);
    if (form.gender === 'F') {
      tgText += `\n👶 GYNAECOLOGICAL\n`;
      tgText += A2('Conditions', checks.gynConditions);
      tgText += L2('Pregnant', form.isPregnant);
    }
    tgText += `\n🧪 INVESTIGATIONS\n`;
    tgText += L2('Family History', form.familyHistory);
    tgText += L2('Lab Investigations', form.labInvestigations);
    if (uploadedFiles.length > 0) tgText += `\n📎 ${uploadedFiles.length} file(s) to follow\n`;
    tgText += `\n${'─'.repeat(36)}\nSubmitted via AIIMR Patient Form`;
    setTgMessage(tgText);
    setTgCopied(false);
    setTgSendModal(true);
  };
  return <div style={{
    fontFamily: 'Inter,-apple-system,sans-serif',
    background: C.parchment,
    minHeight: '100vh',
    color: C.ink,
    WebkitFontSmoothing: 'antialiased'
  }} data-id="element-1">
      <div style={{
      maxWidth: 900,
      margin: '0 auto',
      padding: '24px 16px'
    }} data-id="element-2">
        {/* HEADER */}
        <header style={{
        textAlign: 'center',
        padding: '32px 20px 28px',
        marginBottom: 32,
        background: 'linear-gradient(135deg,#8B1A1A 0%,#6B0F0F 100%)',
        borderRadius: 16,
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(139,26,26,0.3)'
      }} data-id="element-3">
          {/* gold top line */}
          <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: `linear-gradient(90deg,${C.gold},#FFE08A,${C.gold})`
        }} data-id="element-4" />
          {/* lotus deco */}
          <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          marginBottom: 16
        }} data-id="element-5">
            <div style={{
            flex: 1,
            maxWidth: 120,
            height: 1,
            background: `linear-gradient(90deg,transparent,${C.goldLight})`
          }} data-id="element-6" />
            <svg viewBox="0 0 80 80" fill="none" style={{
            width: 40,
            height: 40,
            opacity: 0.9
          }} data-id="element-7">
              <path d="M40 70 C40 70 20 55 20 38 C20 28 30 22 40 30 C50 22 60 28 60 38 C60 55 40 70 40 70Z" fill="rgba(201,168,76,0.35)" stroke="rgba(201,168,76,0.8)" strokeWidth="1.5" data-id="element-8" />
              <path d="M40 70 C40 70 10 52 8 35 C6 22 20 18 30 28 C35 20 40 30 40 30 C40 30 30 20 32 10 C36 2 44 2 48 10 C50 20 40 30 40 30 C40 30 45 20 50 28 C60 18 74 22 72 35 C70 52 40 70 40 70Z" fill="rgba(201,168,76,0.2)" stroke="rgba(201,168,76,0.7)" strokeWidth="1" data-id="element-9" />
              <circle cx="40" cy="42" r="5" fill="rgba(201,168,76,0.6)" stroke="rgba(201,168,76,0.9)" strokeWidth="1" data-id="element-10" />
            </svg>
            <div style={{
            flex: 1,
            maxWidth: 120,
            height: 1,
            background: `linear-gradient(90deg,${C.goldLight},transparent)`
          }} data-id="element-11" />
          </div>
          <h1 style={{
          fontFamily: 'Georgia,serif',
          fontSize: 22,
          fontWeight: 700,
          color: '#FFF8EC',
          marginBottom: 8,
          lineHeight: 1.3
        }} data-id="element-12">
            Aatmshakti Institute of Integrated Medical Research
          </h1>
          <p style={{
          fontSize: 12,
          color: 'rgba(255,240,200,0.8)',
          fontStyle: 'italic',
          marginBottom: 2
        }} data-id="element-13">
            Plot No 41-42, Keshav Nagar, Lucknow U.P. Pin 226020
          </p>
          <p style={{
          fontSize: 12,
          color: 'rgba(255,240,200,0.8)',
          fontStyle: 'italic'
        }} data-id="element-14">
            Mobile: +91 8303699990, 9026625754
          </p>
          <div style={{
          marginTop: 20,
          paddingTop: 16,
          borderTop: '1px solid rgba(201,168,76,0.35)'
        }} data-id="element-15">
            <h2 style={{
            fontFamily: 'Georgia,serif',
            fontSize: 18,
            fontWeight: 600,
            color: C.goldLight,
            marginBottom: 6,
            letterSpacing: 1
          }} data-id="element-16">
              Patient Registration &amp; Examination Form
            </h2>
            <p style={{
            fontSize: 12,
            color: 'rgba(255,240,200,0.7)',
            fontStyle: 'italic',
            letterSpacing: '0.5px'
          }} data-id="element-17">
              ☯ Traditional Chinese Medicine · Acupuncture · Holistic Healing
              ☯
            </p>
          </div>
        </header>

        <form onSubmit={handleWhatsApp} noValidate data-id="element-18">
          {/* 1. PERSONAL DETAILS */}
          <div style={cardStyle} data-id="element-19">
            <div style={cardHeaderStyle} data-id="element-20">
              <SectionHeader icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-22">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" data-id="element-23" />
                    <circle cx="12" cy="7" r="4" data-id="element-24" />
                  </svg>} title="Personal Details" subtitle="Patient information and contact details" data-id="element-21" />
            </div>
            <div style={{
            ...cardBodyStyle,
            display: 'flex',
            flexDirection: 'column',
            gap: 16
          }} data-id="element-25">
              <Grid cols={3} data-id="element-26">
                <FormGroup label="Are You" data-id="element-27">
                  <FSelect value={form.areYou} onChange={sf('areYou')} data-id="element-28">
                    <option value="" data-id="element-29">Select</option>
                    {['Patient', 'Acupuncture Student', 'Acupuncturist', 'Therapist'].map(o => <option key={o} data-id="element-30">{o}</option>)}
                  </FSelect>
                </FormGroup>
                <div style={{
                gridColumn: 'span 2',
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }} data-id="element-31">
                  <FLabel data-id="element-32">Branch Location</FLabel>
                  <FSelect value={form.location} onChange={sf('location')} data-id="element-33">
                    <option value="" data-id="element-34">Select Branch Location</option>
                    <option value="Self Treatment (N/A)" data-id="element-35">
                      Self Treatment (N/A)
                    </option>
                    <option value="Plot No 41-42, Keshav Nagar, Lucknow (Dr. Amit Kapoor)" data-id="element-36">
                      Lucknow — Plot No 41-42, Keshav Nagar (Dr. Amit Kapoor)
                      +91 8303699990
                    </option>
                    <option value="Adeshwar kripa #6, Shanti Nagar, Bangalore 560027 (Sheeetal M Bohra)" data-id="element-37">
                      Bangalore — Shanti Nagar (Sheeetal M Bohra) +91 9632132911
                    </option>
                    <option value="A305 Shriram Southern Crest, JP Nagar 6th Phase, Bangalore 560078 (Sunitanjani Miriyala)" data-id="element-38">
                      Bangalore — JP Nagar (Sunitanjani Miriyala) +91 8050158700
                    </option>
                    <option value="179, Ramesh Nagar, Marathalli, Bangalore 560037 (Mithun M Vernekar)" data-id="element-39">
                      Bangalore — Marathalli (Mithun M Vernekar) 9845856582
                    </option>
                    <option value="B-319 Garden City, Ankleshwar, Gujarat 393002 (Anisha Yogesh Potdar)" data-id="element-40">
                      Gujarat — Ankleshwar (Anisha Yogesh Potdar) +91 9909209557
                    </option>
                    <option value="501 Swarnvilla 1, VIP Road Althan Surat Gujarat (Sangeeta Nahata)" data-id="element-41">
                      Surat — Althan (Sangeeta Nahata) +91 9376630760
                    </option>
                  </FSelect>
                </div>
              </Grid>

              <Grid cols={2} data-id="element-42">
                <FormGroup label="📅 Admission Date" data-id="element-43">
                  <FInput type="date" value={form.admissionDate} onChange={sf('admissionDate')} data-id="element-44" />
                </FormGroup>
                <FormGroup label="Reference" data-id="element-45">
                  <FInput value={form.reference} onChange={sf('reference')} placeholder="Reference source" data-id="element-46" />
                </FormGroup>
              </Grid>

              <Grid cols={3} data-id="element-47">
                <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }} data-id="element-48">
                  <FLabel data-id="element-49">Patient Name *</FLabel>
                  <FInput value={form.name} onChange={sf('name')} placeholder="Full name" data-id="element-50" />
                  {errors.name && <span style={{
                  fontSize: 12,
                  color: '#dc2626'
                }} data-id="element-51">
                      {errors.name}
                    </span>}
                </div>
                <FormGroup label="Gender" data-id="element-52">
                  <FSelect value={form.gender} onChange={sf('gender')} data-id="element-53">
                    <option value="" data-id="element-54">Select</option>
                    <option value="M" data-id="element-55">Male</option>
                    <option value="F" data-id="element-56">Female</option>
                    <option value="Other" data-id="element-57">Other</option>
                  </FSelect>
                </FormGroup>
                <FormGroup label="Age" data-id="element-58">
                  <FInput type="number" value={form.age} onChange={sf('age')} placeholder="Years" data-id="element-59" />
                </FormGroup>
              </Grid>

              <Grid cols={3} data-id="element-60">
                <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4
              }} data-id="element-61">
                  <FLabel data-id="element-62">Mobile *</FLabel>
                  <FInput type="tel" value={form.mobile} onChange={sf('mobile')} placeholder="Number" data-id="element-63" />
                  {errors.mobile && <span style={{
                  fontSize: 12,
                  color: '#dc2626'
                }} data-id="element-64">
                      {errors.mobile}
                    </span>}
                </div>
                <FormGroup label="Guardian Name" data-id="element-65">
                  <FInput value={form.guardianName} onChange={sf('guardianName')} placeholder="Name" data-id="element-66" />
                </FormGroup>
                <FormGroup label="Occupation" data-id="element-67">
                  <FInput value={form.occupation} onChange={sf('occupation')} placeholder="Current occupation" data-id="element-68" />
                </FormGroup>
              </Grid>
            </div>

            {/* Address block */}
            <div style={{
            background: C.goldPale,
            border: `1px solid ${C.goldBorder}`,
            borderRadius: 10,
            padding: 16,
            margin: '0 20px 20px'
          }} data-id="element-69">
              <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontFamily: 'Georgia,serif',
              fontSize: 14,
              fontWeight: 700,
              color: C.crimson,
              marginBottom: 12
            }} data-id="element-70">
                <svg viewBox="0 0 24 24" fill="none" stroke={C.crimsonMid} strokeWidth="2" style={{
                width: 18,
                height: 18
              }} data-id="element-71">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" data-id="element-72" />
                  <circle cx="12" cy="10" r="3" data-id="element-73" />
                </svg>
                Address Details
              </div>
              <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12
            }} data-id="element-74">
                <FormGroup label="Address" data-id="element-75">
                  <FInput value={form.address} onChange={sf('address')} placeholder="Complete address" data-id="element-76" />
                </FormGroup>
                <Grid cols={2} data-id="element-77">
                  <FormGroup label="City" data-id="element-78">
                    <FInput value={form.city} onChange={sf('city')} placeholder="City" data-id="element-79" />
                  </FormGroup>
                  <FormGroup label="Pincode" data-id="element-80">
                    <FInput value={form.pincode} onChange={sf('pincode')} placeholder="Code" data-id="element-81" />
                  </FormGroup>
                </Grid>
                <FormGroup label="Chief Complaints" data-id="element-82">
                  <FTextarea value={form.chiefComplaints} onChange={sf('chiefComplaints')} rows={6} placeholder="Describe main health concerns and symptoms..." data-id="element-83" />
                </FormGroup>
              </div>
            </div>
          </div>

          {/* 2. PHYSICAL EXAMINATION */}
          <div style={cardStyle} data-id="element-84">
            <div style={cardHeaderStyle} data-id="element-85">
              <SectionHeader icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-87">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" data-id="element-88" />
                  </svg>} title="Physical Examination" subtitle="General survey and objective findings" data-id="element-86" />
            </div>
            <div style={{
            ...cardBodyStyle,
            display: 'flex',
            flexDirection: 'column',
            gap: 16
          }} data-id="element-89">
              <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3,1fr)',
              gap: 16
            }} data-id="element-90">
                {([['Thirst', 'thirst', ['1-2 Glass', '3-4 Glass', '4-5 Glass', '6-7 Glass', '7-8 Glass', '8-10 Glass', '10+ Glass']], ['Appetite', 'appetite', ['Normal Appetite', 'Poor / Loss of Appetite', 'Excess Appetite', "Hungry but can't eat"]], ['Sleep', 'sleep', ['Normal Sleep', 'Difficulty Falling Asleep', 'Difficulty after Awake', 'Frequent Waking', 'Light / Unrestful', 'Dreams Disturbed']], ['Dreams', 'dreams', ['No Dreams', 'Vivid / Disturbed', 'Nightmares', 'Dream Categories']], ['Blood Pressure', 'bp', ['Normal', 'Low', 'High']], ['Temperature', 'temperature', ['Normal', 'Low', 'High']], ['Life Style', 'lifestyle', ['Sedentary lifestyle', 'Lightly Active', 'Moderately Active', 'Vigorously Active']], ['Body Type', 'bodyType', ['Underweight', 'Normal weight', 'Overweight', 'Obese', 'Huge']]] as [string, keyof FormState, string[]][]).map(([label, key, opts]) => <FormGroup key={key} label={label} data-id="element-91">
                    <FSelect value={form[key]} onChange={sf(key)} data-id="element-92">
                      <option value="" data-id="element-93">Select</option>
                      {opts.map(o => <option key={o} data-id="element-94">{o}</option>)}
                    </FSelect>
                  </FormGroup>)}
                <FormGroup label="Addiction" data-id="element-95">
                  <FInput value={form.addiction} onChange={sf('addiction')} data-id="element-96" />
                </FormGroup>
                <FormGroup label="Weight (kg)" data-id="element-97">
                  <FInput value={form.weight} onChange={sf('weight')} placeholder="kg" data-id="element-98" />
                </FormGroup>
                <FormGroup label="Hygiene" data-id="element-99">
                  <FInput value={form.hygiene} onChange={sf('hygiene')} data-id="element-100" />
                </FormGroup>
              </div>

              {/* Mental + Food */}
              <Grid cols={2} data-id="element-101">
                <CheckBox title="Mental Condition" data-id="element-102">
                  <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2,1fr)',
                  gap: 10
                }} data-id="element-103">
                    {['Normal', 'Good', 'Tension', 'Anxiety', 'Confused', 'Worried', 'Depressed', 'Other'].map(v => <CheckItem key={v} label={v} checked={checks.mentalCondition.includes(v)} onChange={checked => {
                    chk('mentalCondition')(v, checked);
                    if (v === 'Other') setShowMentalOther(checked);
                  }} data-id="element-104" />)}
                  </div>
                  {showMentalOther && <div style={{
                  marginTop: 10
                }} data-id="element-105">
                      <FInput value={mentalOther} onChange={setMentalOther} placeholder="Please specify..." data-id="element-106" />
                    </div>}
                </CheckBox>
                <CheckBox title="Food Habits and Nutrition" data-id="element-107">
                  <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12
                }} data-id="element-108">
                    {['Take Fruits and Dry fruits & Nuts (soaked)', 'Take 3 Times Healthy Food', "Don't take water before/after meal (45 min)", 'Take water before/after meal (45 min)'].map(v => <CheckItem key={v} label={v} checked={checks.foodHabits.includes(v)} onChange={checked => chk('foodHabits')(v, checked)} data-id="element-109" />)}
                  </div>
                </CheckBox>
              </Grid>

              <Divider data-id="element-110" />

              <Grid cols={3} data-id="element-111">
                {([['Body Palpation', 'bodyPalpation'], ['Tongue', 'tongue'], ['Pulse', 'pulse']] as [string, keyof FormState][]).map(([label, key]) => <FormGroup key={key} label={label} data-id="element-112">
                    <FInput value={form[key]} onChange={sf(key)} data-id="element-113" />
                  </FormGroup>)}
              </Grid>
            </div>
          </div>

          {/* 3. MEDICAL HISTORY */}
          <div style={{
          marginBottom: 16
        }} data-id="element-114">
            <StandaloneTitle icon={<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5 19.79 19.79 0 0 1 1.61 5a2 2 0 0 1 1.99-2H6.6a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.09a16 16 0 0 0 6 6l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.72 17z" data-id="element-116" />} data-id="element-115">
              Medical History
            </StandaloneTitle>
            <CheckBox data-id="element-117">
              <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4,1fr)',
              gap: 10
            }} data-id="element-118">
                {['None', 'Pre-Diabetic', 'Kidney Problem', 'Arthritis', 'HIV/AIDS', 'Stroke', "Alzheimer's", 'Organ Transplant', 'Diabetic', 'Heart Problem', 'High BP', 'Cancer', 'Hepatitis', 'Paralysis', 'Asthma'].map(v => <CheckItem key={v} label={v} checked={checks.medicalHistory.includes(v)} onChange={checked => chk('medicalHistory')(v, checked)} data-id="element-119" />)}
              </div>
            </CheckBox>
            <div style={{
            ...cardStyle,
            marginTop: 12
          }} data-id="element-120">
              <div style={cardBodyStyle} data-id="element-121">
                <FormGroup label="History of Past Illness" data-id="element-122">
                  <FInput value={form.pastIllness} onChange={sf('pastIllness')} placeholder="Previous major illnesses, surgeries, etc." data-id="element-123" />
                </FormGroup>
              </div>
            </div>
          </div>

          {/* 4. OBJECTIVE FINDINGS */}
          <div style={{
          marginBottom: 16
        }} data-id="element-124">
            <StandaloneTitle icon={<>
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" data-id="element-126" />
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1" data-id="element-127" />
                </>} data-id="element-125">
              Objective Findings
            </StandaloneTitle>
            <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3,1fr)',
            gap: 16
          }} data-id="element-128">
              {/* Selects */}
              {([['1. Bowel Movement', 'bowel', ['1 time (morning)', '1 Time (Any time)', '2 Times (Morning/Evening)', 'After meal', 'Before meal', 'Other']], ['2. Urine Freq (Day)', 'urineFrequency', ['No', 'Normal', 'Excess', 'Less']], ['3. Urine Freq (Night)', 'urineFrequencyNight', ['No', 'Normal', 'Excess', 'Less']], ['4. Urine Color', 'urineColor', ['Pale Yellow', 'Dark Yellow', 'Orange', 'Pink or Red', 'Green or Blue', 'Brown', 'White']], ['5. Urine Smell', 'urineSmell', ['Normal Smell', 'Strong Smell']], ['7. Skin', 'skin', ['Normal Skin', 'Oily Skin', 'Dry Skin', 'Combination', 'Sensitive']], ['8. Scalp (Hair)', 'scalp', ['Normal/Balanced', 'Oily Scalp', 'Dry Scalp', 'Sensitive', 'Combination', 'Early Baldness', 'Hair Fall', 'Early Grey Hair', 'Dandruff']], ['9. Body (Hair)', 'bodyHair', ['No Hair', 'Less Hair', 'Normal Hair', 'Excess Hair']], ['10. Nails', 'nails', ['Normal', 'Dry and brittle', 'Soft and brittle', 'Vertical Lines', 'Horizontal lines', 'Discolored', 'Thick', 'Thin']], ['11. Body Odor', 'bodyOdour', ['Normal / No Odor', 'Fragrant', 'Woody/Resinous', 'Minty', 'Sweet', 'Chemical', 'Popcorn', 'Citrus', 'Fruity', 'Pungent', 'Putrid']], ['12. Edema', 'edema', ['Normal / No Problem', 'Pitting edema', 'Non pitted type']], ['15. Mouth Odor', 'mouthOdour', ['Normal / No Odor', 'Fragrant', 'Woody/Resinous', 'Minty', 'Sweet', 'Chemical', 'Popcorn', 'Citrus', 'Fruity', 'Pungent', 'Putrid']], ['17. Throat', 'throat', ['Normal / No Problem', 'Tonsillitis', 'Strep throat', 'Laryngitis', 'Pharyngitis']], ['18. Lips', 'lips', ['Normal / No Problem', 'Chapped lips', 'Cold sores', 'Angular cheilitis', 'Eczematous', 'Infective', 'Actinic']]] as [string, keyof FormState, string[]][]).map(([label, key, opts]) => <div key={key} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }} data-id="element-129">
                  <label style={{
                ...labelStyle,
                color: C.crimson
              }} data-id="element-130">
                    {label}
                  </label>
                  <FSelect value={form[key]} onChange={sf(key)} data-id="element-131">
                    <option value="" data-id="element-132">Select</option>
                    {opts.map(o => <option key={o} data-id="element-133">{o}</option>)}
                  </FSelect>
                </div>)}
              {/* Text inputs */}
              {([['6. Urine Details', 'urine', 'Burning / Other'], ['13. Eye', 'eye', ''], ['14. Ear', 'ear', ''], ['16. Mouth (Add.)', 'mouth', 'Details']] as [string, keyof FormState, string][]).map(([label, key, ph]) => <div key={key} style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4
            }} data-id="element-134">
                  <label style={{
                ...labelStyle,
                color: C.crimson
              }} data-id="element-135">
                    {label}
                  </label>
                  <FInput value={form[key]} onChange={sf(key)} placeholder={ph} data-id="element-136" />
                </div>)}
            </div>
          </div>

          {/* 5. GYNAECOLOGICAL HISTORY (Female only) */}
          {form.gender === 'F' && <div style={cardStyle} data-id="element-137">
              <div style={cardHeaderStyle} data-id="element-138">
                <SectionHeader icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-140">
                      <path d="M9 12h6m-3-3v6M12 3a9 9 0 1 0 0 18A9 9 0 0 0 12 3z" data-id="element-141" />
                    </svg>} title="Gynaecological History" data-id="element-139" />
              </div>
              <div style={{
            ...cardBodyStyle,
            display: 'flex',
            flexDirection: 'column',
            gap: 16
          }} data-id="element-142">
                <CheckBox data-id="element-143">
                  <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3,1fr)',
                gap: 10
              }} data-id="element-144">
                    {['None', 'Endometriosis', 'Dysmenorrhea (cramps)', 'Menorrhagia (heavy)', 'Adenomyosis', 'Anemia', 'Fibroids / Cysts', 'Cancers: Breast/Ovarian', 'PCOS', 'Amenorrhea', 'PMS', 'UTIs', 'Infertility'].map(v => <CheckItem key={v} label={v} checked={checks.gynConditions.includes(v)} onChange={checked => chk('gynConditions')(v, checked)} data-id="element-145" />)}
                  </div>
                </CheckBox>
                <Grid cols={3} data-id="element-146">
                  <FormGroup label="Pregnant?" data-id="element-147">
                    <FSelect value={form.isPregnant} onChange={sf('isPregnant')} data-id="element-148">
                      <option value="No" data-id="element-149">No</option>
                      <option value="Yes" data-id="element-150">Yes</option>
                    </FSelect>
                  </FormGroup>
                  {form.isPregnant === 'Yes' && <FormGroup label="Months" data-id="element-151">
                      <FInput value={form.pregnancyMonths} onChange={sf('pregnancyMonths')} data-id="element-152" />
                    </FormGroup>}
                  {form.isPregnant === 'No' && <FormGroup label="Menopause" data-id="element-153">
                      <FSelect value={form.menopause} onChange={sf('menopause')} data-id="element-154">
                        <option value="No" data-id="element-155">No</option>
                        <option value="Yes" data-id="element-156">Yes</option>
                      </FSelect>
                    </FormGroup>}
                </Grid>
                {form.isPregnant === 'No' && form.menopause === 'No' && <div style={{
              background: C.goldPale,
              padding: 16,
              borderRadius: 8,
              border: `1px solid ${C.goldBorder}`
            }} data-id="element-157">
                    <div style={{
                fontFamily: 'Georgia,serif',
                color: C.crimson,
                fontWeight: 700,
                fontSize: 14,
                marginBottom: 12
              }} data-id="element-158">
                      Mensuration Details
                    </div>
                    <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3,1fr)',
                gap: 16
              }} data-id="element-159">
                      <FormGroup label="Duration (Days)" data-id="element-160">
                        <FSelect value={form.mensurationDuration} onChange={sf('mensurationDuration')} data-id="element-161">
                          <option value="" data-id="element-162">Select</option>
                          {['Before 16 days', '16 days', '28 days', 'More than 36 days'].map(o => <option key={o} data-id="element-163">{o}</option>)}
                        </FSelect>
                      </FormGroup>
                      <FormGroup label="Cycle Days" data-id="element-164">
                        <FSelect value={form.mensurationCycleDays} onChange={sf('mensurationCycleDays')} data-id="element-165">
                          <option value="" data-id="element-166">Select</option>
                          {['Needs Medicine', '3 Days', '5 Days', '7 Days'].map(o => <option key={o} data-id="element-167">{o}</option>)}
                        </FSelect>
                      </FormGroup>
                      <FormGroup label="Clots" data-id="element-168">
                        <FSelect value={form.mensurationClots} onChange={sf('mensurationClots')} data-id="element-169">
                          <option value="" data-id="element-170">Select</option>
                          <option data-id="element-171">Yes</option>
                          <option data-id="element-172">No</option>
                        </FSelect>
                      </FormGroup>
                      <FormGroup label="Flow Type" data-id="element-173">
                        <FSelect value={form.mensurationFlow} onChange={sf('mensurationFlow')} data-id="element-174">
                          <option value="" data-id="element-175">Select</option>
                          {['Scanty', 'Normal', 'Excess'].map(o => <option key={o} data-id="element-176">{o}</option>)}
                        </FSelect>
                      </FormGroup>
                      <div style={{
                  gridColumn: 'span 2',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4
                }} data-id="element-177">
                        <FLabel data-id="element-178">Painful</FLabel>
                        <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 16,
                    marginTop: 4
                  }} data-id="element-179">
                          {['No Pain', 'Before', 'During', 'After'].map(v => <CheckItem key={v} label={v} checked={checks.mensurationPainful.includes(v)} onChange={checked => chk('mensurationPainful')(v, checked)} data-id="element-180" />)}
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>}

          {/* 6. FAMILY HISTORY + LAB */}
          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2,1fr)',
          gap: 16,
          marginBottom: 16
        }} data-id="element-181">
            {([['Family History', 'familyHistory', 'Family medical history, hereditary conditions...', <svg key="fh" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-182">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" data-id="element-183" />
                    <circle cx="9" cy="7" r="4" data-id="element-184" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" data-id="element-185" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" data-id="element-186" />
                  </svg>], ['Laboratory Investigations', 'labInvestigations', 'Lab test results, investigations...', <svg key="li" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-187">
                    <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" data-id="element-188" />
                  </svg>]] as [string, keyof FormState, string, JSX.Element][]).map(([title, key, ph, icon]) => <div key={key} style={{
            ...cardStyle,
            marginBottom: 0
          }} data-id="element-189">
                <div style={cardHeaderStyle} data-id="element-190">
                  <SectionHeader icon={icon} title={title} data-id="element-191" />
                </div>
                <div style={cardBodyStyle} data-id="element-192">
                  <FTextarea value={form[key]} onChange={sf(key)} rows={8} placeholder={ph} data-id="element-193" />
                </div>
              </div>)}
          </div>

          {/* 7. ATTACHMENTS */}
          <div style={cardStyle} data-id="element-194">
            <div style={cardHeaderStyle} data-id="element-195">
              <SectionHeader icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-197">
                    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" data-id="element-198" />
                  </svg>} title="Attachments" subtitle="Upload tongue photos, reports & images (Max 10, < 5MB each)" data-id="element-196" />
            </div>
            <div style={cardBodyStyle} data-id="element-199">
              <div onClick={() => fileInputRef.current?.click()} onDragOver={e => {
              e.preventDefault();
              setDragOver(true);
            }} onDragLeave={() => setDragOver(false)} onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              handleFiles(e.dataTransfer.files);
            }} style={{
              border: `2px dashed ${dragOver ? C.crimsonMid : C.crimsonBorder}`,
              borderRadius: 10,
              background: dragOver ? C.crimsonHover : C.crimsonPale,
              padding: '28px 20px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s'
            }} data-id="element-200">
                <svg viewBox="0 0 24 24" fill="none" stroke={C.crimsonBorder} strokeWidth="1.5" style={{
                width: 36,
                height: 36,
                margin: '0 auto 10px',
                display: 'block'
              }} data-id="element-201">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-202" />
                  <polyline points="17 8 12 3 7 8" data-id="element-203" />
                  <line x1="12" y1="3" x2="12" y2="15" data-id="element-204" />
                </svg>
                <p style={{
                fontSize: 14,
                color: C.crimsonMid
              }} data-id="element-205">
                  Drag &amp; Drop images here or <strong data-id="element-206">Browse Files</strong>
                </p>
                <p style={{
                fontSize: 11,
                color: '#7a5c3a',
                marginTop: 6
              }} data-id="element-207">
                  Tongue photos (front &amp; back), medical reports, test
                  results
                </p>
              </div>
              <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf" style={{
              display: 'none'
            }} onChange={e => handleFiles(e.target.files)} data-id="element-208" />
              {attachError && <p style={{
              color: '#dc2626',
              fontSize: 12,
              fontWeight: 600,
              marginTop: 8
            }} data-id="element-209">
                  {attachError}
                </p>}
              {uploadedFiles.length > 0 && <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5,1fr)',
              gap: 12,
              marginTop: 16
            }} data-id="element-210">
                  {uploadedFiles.map((f, i) => <div key={i} style={{
                position: 'relative',
                height: 120,
                border: `2px solid ${C.goldBorder}`,
                borderRadius: 8,
                overflow: 'hidden',
                background: '#fff',
                boxShadow: `0 2px 8px rgba(139,26,26,0.1)`
              }} data-id="element-211">
                      {f.type.startsWith('image/') ? <img src={f.data} alt={f.name} style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }} data-id="element-212" /> : <div style={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: C.parchment,
                  fontSize: 11,
                  color: C.inkMid,
                  padding: 6,
                  textAlign: 'center'
                }} data-id="element-213">
                          {f.name}
                        </div>}
                      <button type="button" onClick={() => setUploadedFiles(p => p.filter((_, j) => j !== i))} style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  background: 'rgba(178,34,34,0.85)',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderBottomLeftRadius: 6,
                  fontSize: 14
                }} data-id="element-214">
                        ✕
                      </button>
                    </div>)}
                </div>}
            </div>
          </div>

          {/* 8. PAYMENT */}
          <div style={cardStyle} data-id="element-215">
            <div style={cardHeaderStyle} data-id="element-216">
              <SectionHeader icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-218">
                    <line x1="12" y1="1" x2="12" y2="23" data-id="element-219" />
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" data-id="element-220" />
                  </svg>} title="Consultation Fee Payment" subtitle="Please complete payment before submitting the form" data-id="element-217" />
            </div>
            <div style={cardBodyStyle} data-id="element-221">
              <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 24,
              flexWrap: 'wrap'
            }} data-id="element-222">
                <div style={{
                border: `3px solid ${C.gold}`,
                borderRadius: 12,
                padding: 8,
                background: `linear-gradient(135deg,${C.goldPale},#fff)`,
                boxShadow: `0 4px 16px rgba(139,26,26,0.12)`,
                flexShrink: 0
              }} data-id="element-223">
                  <img src="https://cdn.magicpatterns.com/uploads/gPfGZPjknKRiy2ZgMkCWX3/pasted-image.png" alt="UPI QR Code" style={{
                  width: 200,
                  height: 'auto',
                  borderRadius: 4,
                  display: 'block'
                }} data-id="element-224" />
                </div>
                <div style={{
                flex: 1,
                minWidth: 200
              }} data-id="element-225">
                  <div style={{
                  background: `linear-gradient(135deg,${C.crimson},#6B0F0F)`,
                  borderRadius: 10,
                  padding: '14px 24px',
                  display: 'inline-block',
                  marginBottom: 14,
                  boxShadow: `0 4px 12px rgba(139,26,26,0.25)`
                }} data-id="element-226">
                    <p style={{
                    fontFamily: 'Georgia,serif',
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#FFF8EC',
                    letterSpacing: '0.5px'
                  }} data-id="element-227">
                      Consultation Fee — ₹200
                    </p>
                  </div>
                  <p style={{
                  fontSize: 13,
                  color: C.inkMid,
                  marginBottom: 6
                }} data-id="element-228">
                    Scan the QR code to pay via UPI
                  </p>
                  <p style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: C.crimson
                }} data-id="element-229">
                    UPI ID:{' '}
                    <span style={{
                    userSelect: 'all'
                  }} data-id="element-230">
                      annukapoor@okicici
                    </span>
                  </p>
                  <p style={{
                  fontSize: 12,
                  color: '#7a5c3a',
                  fontStyle: 'italic',
                  marginTop: 10
                }} data-id="element-231">
                    Please make the payment before submitting the form
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 9. CONSENT + ACTIONS */}
          <div style={{
          background: `linear-gradient(135deg,#fff 0%,${C.goldPale} 100%)`,
          border: `2px solid ${C.goldBorder}`,
          borderRadius: 14,
          boxShadow: `0 4px 20px rgba(139,26,26,0.08)`,
          padding: 24,
          marginBottom: 16
        }} data-id="element-232">
            <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            cursor: 'pointer'
          }} data-id="element-233">
              <input type="checkbox" checked={consentAgreed} onChange={e => setConsentAgreed(e.target.checked)} style={{
              width: 20,
              height: 20,
              marginTop: 2,
              accentColor: C.crimsonMid,
              flexShrink: 0
            }} data-id="element-234" />
              <span style={{
              fontSize: 13,
              color: C.inkMid,
              lineHeight: 1.6
            }} data-id="element-235">
                I have read and I agree to the{' '}
                <button type="button" onClick={() => setConsentModal(true)} style={{
                color: C.crimsonMid,
                fontWeight: 700,
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontFamily: 'inherit'
              }} data-id="element-236">
                  Informed Consent Form
                </button>
              </span>
            </label>

            <Divider data-id="element-237" />

            {/* Reminder box */}
            <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            background: `linear-gradient(135deg,#FFF8E7,#FDF3DC)`,
            border: `2px solid ${C.gold}`,
            borderRadius: 10,
            padding: '14px 16px',
            marginBottom: 20
          }} data-id="element-238">
              <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" style={{
              width: 20,
              height: 20,
              flexShrink: 0,
              marginTop: 2
            }} data-id="element-239">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" data-id="element-240" />
                <line x1="12" y1="9" x2="12" y2="13" data-id="element-241" />
                <line x1="12" y1="17" x2="12.01" y2="17" data-id="element-242" />
              </svg>
              <p style={{
              fontSize: 13,
              color: C.inkMid,
              lineHeight: 1.7
            }} data-id="element-243">
                Don't Forget to send your{' '}
                <strong style={{
                color: C.crimson
              }} data-id="element-244">
                  Tongue Picture Front &amp; Back
                </strong>{' '}
                (lower side of tongue) and{' '}
                <strong style={{
                color: C.crimson
              }} data-id="element-245">
                  latest medical reports
                </strong>{' '}
                if any
              </p>
            </div>

            <div style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center'
          }} data-id="element-246">
              <Btn variant="tg" onClick={handleTelegram} data-id="element-247">
                <svg viewBox="0 0 24 24" fill="currentColor" style={{
                width: 16,
                height: 16
              }} data-id="element-248">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-249" />
                </svg>
                Submit via Telegram
              </Btn>
              <Btn type="submit" variant="wa" data-id="element-250">
                <svg viewBox="0 0 24 24" fill="currentColor" style={{
                width: 16,
                height: 16
              }} data-id="element-251">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-252" />
                </svg>
                Submit via WhatsApp
              </Btn>
            </div>

            <p style={{
            fontSize: 11,
            color: '#7a5c3a',
            fontStyle: 'italic',
            marginTop: 12,
            lineHeight: 1.6
          }} data-id="element-253">
              * <strong data-id="element-254">Submit via Telegram</strong> — copies all form data to
              clipboard &amp; opens @annukapoor on Telegram. If files uploaded,
              a download helper appears so you can share them in the same chat.
              <br data-id="element-255" />* <strong data-id="element-256">Submit via WhatsApp</strong> — sends the complete
              form text to Dr. Amit Kapoor (+91 8303699990). If images are
              uploaded, a helper modal will guide you to share them in WhatsApp.
            </p>
          </div>
        </form>
      </div>

      {/* CONSENT MODAL */}
      <Modal open={consentModal} onClose={() => setConsentModal(false)} title="Informed Consent Form (Acupuncture Treatment)" footer={<Btn onClick={() => setConsentModal(false)} data-id="element-258">Close</Btn>} maxWidth={860} data-id="element-257">
        <div style={{
        fontSize: 13,
        color: C.inkMid,
        lineHeight: 1.7
      }} data-id="element-259">
          <p style={{
          marginBottom: 10
        }} data-id="element-260">
            I understand that the professional practitioners (acupuncturists) in{' '}
            <strong data-id="element-261">
              Aatmshakti Institute of Integrated Medical Research (AIIMR)
            </strong>{' '}
            will treat my conditions within their scope of practice of
            Traditional Chinese Medicine (TCM)/ Master Tung's Acupuncture /
            Auricular Medicine / Dr. Tan Balance Acupuncture Method /
            Acupressure Therapy / Colour Therapy / Korean Cosmetic Acupuncture /
            Abdominal Acupuncture / Scalp Acupuncture / Static Cupping / Moving
            Cupping / Fire Cupping / Blood Letting.
          </p>
          <p style={{
          marginBottom: 10
        }} data-id="element-262">
            I understand that "Acupuncture" treatment involves the penetration
            of skin by sterile, one-time-use, disposable filiform needles. The
            scope of treatment includes acupressure, therapeutic massage
            (Tui-Na), dietary herbal supplements, nutritional counseling,
            energy-flow work (Qigong, Tai Chi), heat therapy / moxibustion,
            fire-cupping, Gua Sha, electrical stimulation, and LED light
            therapy.
          </p>
          <p style={{
          marginBottom: 10
        }} data-id="element-263">
            I understand I must inform practitioners of medications, medical
            history, sensitivities, pregnancy, bleeding disorders or contagious
            conditions. I have the right to refuse treatment at any point.
          </p>
          <h4 style={{
          fontFamily: 'Georgia,serif',
          color: C.crimson,
          fontSize: 15,
          fontWeight: 700,
          margin: '20px 0 10px'
        }} data-id="element-264">
            Risks and Complications
          </h4>
          <p style={{
          marginBottom: 10
        }} data-id="element-265">
            <strong data-id="element-266">Pain</strong> — Needling may cause an initial prick followed
            by temporary achy sensation, numbness, or tingling. Tui-Na, cupping,
            Gua Sha and electro-acupuncture may also cause pain.
          </p>
          <p style={{
          marginBottom: 10
        }} data-id="element-267">
            <strong data-id="element-268">Bruising</strong> — May occur anytime skin is punctured.
            Cupping and Gua Sha intentionally leave red marks that resolve in
            5–10 days.
          </p>
          <p style={{
          marginBottom: 10
        }} data-id="element-269">
            <strong data-id="element-270">Infection</strong> — Possible despite single-use disposable
            needles and clean needle technique.
          </p>
          <p style={{
          marginBottom: 10
        }} data-id="element-271">
            <strong data-id="element-272">Burns &amp; Smoke</strong> — Heat therapy/moxibustion may
            cause redness or burns. Smoke may cause coughing or irritation.
          </p>
          <p style={{
          marginBottom: 10
        }} data-id="element-273">
            <strong data-id="element-274">Dizziness</strong> — Ensure you have eaten and avoided
            alcohol before treatment.
          </p>
          <h4 style={{
          fontFamily: 'Georgia,serif',
          color: C.crimson,
          fontSize: 15,
          fontWeight: 700,
          margin: '20px 0 10px'
        }} data-id="element-275">
            Consent
          </h4>
          <p style={{
          marginBottom: 10
        }} data-id="element-276">
            I may verbally revoke consent to any treatment at any time. I agree
            to waive any rights for legal liability against AIIMR and/or the
            acupuncture therapist.{' '}
            <strong data-id="element-277">
              By agreeing, I confirm I have read, understood, and agree to the
              above Terms of Treatment.
            </strong>
          </p>
        </div>
      </Modal>

      {/* CHARTS MODAL */}
      <Modal open={chartsModal} onClose={() => setChartsModal(false)} title="Reflexology Charts" footer={<Btn onClick={() => setChartsModal(false)} data-id="element-279">Close</Btn>} maxWidth={1000} data-id="element-278">
        <h3 style={{
        fontFamily: 'Georgia,serif',
        color: C.crimson,
        marginBottom: 12
      }} data-id="element-280">
          Hand Reflexology Points
        </h3>
        <img src="https://cdn.magicpatterns.com/uploads/k9qNQyrcEQXgFEsQ4rUdgd/image.png" alt="Hand Reflexology Chart" style={{
        width: '100%',
        height: 'auto',
        borderRadius: 8,
        border: `2px solid ${C.goldBorder}`,
        marginBottom: 24
      }} data-id="element-281" />
        <h3 style={{
        fontFamily: 'Georgia,serif',
        color: C.crimson,
        marginBottom: 12
      }} data-id="element-282">
          Foot Reflexology Points
        </h3>
        <img src="https://cdn.magicpatterns.com/uploads/84cAa2tfEex6DD1CTENWm1/image.png" alt="Foot Reflexology Chart" style={{
        width: '100%',
        height: 'auto',
        borderRadius: 8,
        border: `2px solid ${C.goldBorder}`
      }} data-id="element-283" />
      </Modal>

      {/* WA IMAGES HELPER MODAL */}
      <Modal open={waImagesModal} onClose={() => setWaImagesModal(false)} title="📸 Send Images via WhatsApp" footer={<Btn variant="wa" onClick={() => setWaImagesModal(false)} data-id="element-285">
            <svg viewBox="0 0 24 24" fill="currentColor" style={{
        width: 15,
        height: 15
      }} data-id="element-286">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" data-id="element-287" />
            </svg>
            Done
          </Btn>} maxWidth={640} data-id="element-284">
        <div style={{
        background: `linear-gradient(135deg,#F0FFF4,#DCFCE7)`,
        border: `2px solid ${C.waGreen}`,
        borderRadius: 10,
        padding: '14px 16px',
        marginBottom: 16,
        fontSize: 13,
        color: '#14532d',
        lineHeight: 1.7
      }} data-id="element-288">
          <strong data-id="element-289">Your form text was sent to WhatsApp.</strong> WhatsApp cannot
          receive images from websites directly — follow these steps to send
          your images:
          <ol style={{
          paddingLeft: 20,
          marginTop: 8
        }} data-id="element-290">
            <li data-id="element-291">
              Click <strong data-id="element-292">"⬇ Save Image"</strong> below for each image you
              want to share
            </li>
            <li data-id="element-293">
              Switch to the <strong data-id="element-294">WhatsApp chat</strong> that just opened with
              Dr. Amit Kapoor
            </li>
            <li data-id="element-295">
              Tap the <strong data-id="element-296">attachment (📎)</strong> icon → select the saved
              image(s) → send
            </li>
          </ol>
        </div>
        <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3,1fr)',
        gap: 12
      }} data-id="element-297">
          {uploadedFiles.map((file, i) => <div key={i} style={{
          borderRadius: 8,
          overflow: 'hidden',
          border: `2px solid ${C.goldBorder}`,
          background: '#fff'
        }} data-id="element-298">
              {file.type.startsWith('image/') ? <img src={file.data} alt={file.name} style={{
            width: '100%',
            height: 110,
            objectFit: 'cover',
            display: 'block'
          }} data-id="element-299" /> : <div style={{
            height: 110,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: C.parchment,
            fontSize: 11,
            padding: 8,
            textAlign: 'center',
            color: C.inkMid
          }} data-id="element-300">
                  {file.name}
                </div>}
              <a href={file.data} download={file.name} style={{
            display: 'block',
            width: '100%',
            padding: '7px 6px',
            textAlign: 'center',
            fontSize: 11,
            fontWeight: 600,
            background: C.waGreen,
            color: '#fff',
            textDecoration: 'none'
          }} data-id="element-301">
                ⬇ Save Image
              </a>
            </div>)}
        </div>
      </Modal>

      {/* TELEGRAM SEND MODAL */}
      {tgSendModal && <div onClick={e => {
      if (e.target === e.currentTarget) setTgSendModal(false);
    }} style={{
      position: 'fixed',
      inset: 0,
      zIndex: 100,
      background: 'rgba(26,8,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 16,
      backdropFilter: 'blur(3px)'
    }} data-id="element-302">
          <div style={{
        background: '#fff',
        borderRadius: 16,
        width: '100%',
        maxWidth: 640,
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: `0 24px 80px rgba(26,94,180,0.3), 0 0 0 1px rgba(34,158,217,0.3)`
      }} data-id="element-303">
            {/* Header */}
            <div style={{
          background: `linear-gradient(135deg,${C.tgBlue},${C.tgDark})`,
          color: '#fff',
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          borderBottom: '3px solid #7DD3FC'
        }} data-id="element-304">
              <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }} data-id="element-305">
                <svg viewBox="0 0 24 24" fill="currentColor" style={{
              width: 22,
              height: 22
            }} data-id="element-306">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-307" />
                </svg>
                <h2 style={{
              fontFamily: 'Georgia,serif',
              fontSize: 17,
              fontWeight: 700
            }} data-id="element-308">
                  Send via Telegram to @annukapoor
                </h2>
              </div>
              <button onClick={() => setTgSendModal(false)} style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.8)',
            cursor: 'pointer',
            padding: 6,
            borderRadius: 6,
            display: 'flex'
          }} data-id="element-309">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
              width: 22,
              height: 22
            }} data-id="element-310">
                  <line x1="18" y1="6" x2="6" y2="18" data-id="element-311" />
                  <line x1="6" y1="6" x2="18" y2="18" data-id="element-312" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: 20,
          background: C.parchment
        }} data-id="element-313">
              {/* Step instructions */}
              <div style={{
            background: 'linear-gradient(135deg,#EFF6FF,#DBEAFE)',
            border: `2px solid ${C.tgBlue}`,
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13,
            color: '#1e3a5f',
            lineHeight: 1.7
          }} data-id="element-314">
                <strong data-id="element-315">3 easy steps to send your form:</strong>
                <ol style={{
              paddingLeft: 20,
              marginTop: 6
            }} data-id="element-316">
                  <li data-id="element-317">
                    Click <strong data-id="element-318">"📋 Copy Message"</strong> below — the full
                    form text is copied
                  </li>
                  <li data-id="element-319">
                    Click <strong data-id="element-320">"Open @annukapoor on Telegram"</strong> — the
                    chat opens in a new tab
                  </li>
                  <li data-id="element-321">
                    In Telegram, paste the message{' '}
                    <strong data-id="element-322">(Ctrl+V / Cmd+V)</strong> and press Send
                  </li>
                </ol>
              </div>

              {/* Copy button */}
              <button onClick={() => {
            const ta = document.getElementById('tg-msg-area') as HTMLTextAreaElement;
            if (ta) {
              ta.focus();
              ta.select();
              try {
                document.execCommand('copy');
              } catch (e) {}
            }
            if (navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(tgMessage).catch(() => {});
            }
            setTgCopied(true);
            setTimeout(() => setTgCopied(false), 4000);
          }} style={{
            width: '100%',
            padding: '13px 20px',
            marginBottom: 10,
            background: tgCopied ? 'linear-gradient(135deg,#15803d,#166534)' : `linear-gradient(135deg,${C.tgBlue},${C.tgDark})`,
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'background 0.3s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            fontFamily: 'inherit'
          }} data-id="element-323">
                {tgCopied ? <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{
                width: 18,
                height: 18
              }} data-id="element-324">
                      <polyline points="20 6 9 17 4 12" data-id="element-325" />
                    </svg>{' '}
                    Copied! Now open Telegram and Paste
                  </> : <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{
                width: 18,
                height: 18
              }} data-id="element-326">
                      <rect x="9" y="9" width="13" height="13" rx="2" data-id="element-327" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" data-id="element-328" />
                    </svg>{' '}
                    📋 Copy Message
                  </>}
              </button>

              {/* Open Telegram button */}
              <a href="https://t.me/annukapoor" target="_blank" rel="noreferrer" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            width: '100%',
            padding: '13px 20px',
            marginBottom: 16,
            background: 'linear-gradient(135deg,#0f766e,#0d9488)',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 700,
            textDecoration: 'none',
            boxSizing: 'border-box'
          }} data-id="element-329">
                <svg viewBox="0 0 24 24" fill="currentColor" style={{
              width: 18,
              height: 18
            }} data-id="element-330">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-331" />
                </svg>
                Open @annukapoor on Telegram ↗
              </a>

              {/* Scrollable message preview */}
              <div style={{
            fontSize: 12,
            color: '#7a5c3a',
            fontWeight: 600,
            marginBottom: 6
          }} data-id="element-332">
                📄 Message Preview — select all &amp; copy manually if needed:
              </div>
              <textarea id="tg-msg-area" readOnly value={tgMessage} style={{
            width: '100%',
            height: 200,
            padding: 12,
            fontSize: 11,
            fontFamily: 'monospace',
            background: '#fff',
            border: `1px solid ${C.parchmentDark}`,
            borderRadius: 8,
            resize: 'none',
            color: C.inkMid,
            lineHeight: 1.6,
            boxSizing: 'border-box'
          }} onClick={e => (e.target as HTMLTextAreaElement).select()} data-id="element-333" />
              <p style={{
            fontSize: 11,
            color: '#7a5c3a',
            marginTop: 6,
            fontStyle: 'italic'
          }} data-id="element-334">
                Tip: Click inside the box above to select all text, then copy
                manually if the button above doesn't work.
              </p>

              {/* File downloads if any */}
              {uploadedFiles.length > 0 && <>
                  <div style={{
              borderTop: `1px solid ${C.parchmentDark}`,
              margin: '16px 0'
            }} data-id="element-335" />
                  <div style={{
              fontSize: 13,
              fontWeight: 700,
              color: C.tgBlue,
              marginBottom: 10
            }} data-id="element-336">
                    📎 After sending the message, also share these files in the
                    same Telegram chat:
                  </div>
                  <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3,1fr)',
              gap: 10
            }} data-id="element-337">
                    {uploadedFiles.map((file, i) => <div key={i} style={{
                borderRadius: 8,
                overflow: 'hidden',
                border: `2px solid ${C.goldBorder}`,
                background: '#fff'
              }} data-id="element-338">
                        {file.type.startsWith('image/') ? <img src={file.data} alt={file.name} style={{
                  width: '100%',
                  height: 90,
                  objectFit: 'cover',
                  display: 'block'
                }} data-id="element-339" /> : <div style={{
                  height: 90,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: C.parchment,
                  fontSize: 10,
                  padding: 6,
                  textAlign: 'center',
                  color: C.inkMid
                }} data-id="element-340">
                            {file.name}
                          </div>}
                        <a href={file.data} download={file.name} style={{
                  display: 'block',
                  width: '100%',
                  padding: '6px',
                  textAlign: 'center',
                  fontSize: 11,
                  fontWeight: 600,
                  background: C.tgBlue,
                  color: '#fff',
                  textDecoration: 'none'
                }} data-id="element-341">
                          ⬇ Download
                        </a>
                      </div>)}
                  </div>
                </>}
            </div>

            {/* Footer */}
            <div style={{
          borderTop: `2px solid ${C.goldBorder}`,
          padding: '14px 20px',
          background: C.goldPale,
          flexShrink: 0
        }} data-id="element-342">
              <button onClick={() => setTgSendModal(false)} style={{
            padding: '10px 24px',
            background: `linear-gradient(135deg,${C.crimson},#6B0F0F)`,
            color: '#FFF8EC',
            border: `2px solid ${C.crimson}`,
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit'
          }} data-id="element-343">
                Close
              </button>
            </div>
          </div>
        </div>}

      {/* TOAST NOTIFICATION */}
      {toast && <div style={{
      position: 'fixed',
      bottom: 28,
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#1a0800',
      color: '#FFF8EC',
      padding: '12px 24px',
      borderRadius: 10,
      fontSize: 13,
      fontWeight: 600,
      zIndex: 200,
      boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
      border: `1px solid ${C.gold}`,
      maxWidth: '90vw',
      textAlign: 'center',
      animation: 'fadeInUp 0.3s ease'
    }} data-id="element-344">
          {toast}
        </div>}
    </div>;
}